import React from "react";

import {
  AsyncSelect,
  CheckForm,
  Grid,
  Group,
  Input,
  Section,
  Select,
  Typography,
} from "@samskip/frontend-components";
import {
  Control,
  DeepRequired,
  FieldErrorsImpl,
  UseFormRegister,
  useWatch,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

import { LabelConstants, DomesticConstants } from "@assets/constants";
import { DomesticFormTypes } from "@assets/formTypes";
import {
  DomesticTypes,
  UserProfileViewModel,
  CommonTypes,
} from "@assets/types";
import {
  departmentLabelMapper,
  departmentValueMapper,
  postCodeLabelMapper,
  postCodeValueMapper,
  customerCompanyLabelMapper,
  customerCompanyValueMapper,
} from "@assets/utilities/labelMapperUtils";
import { getErrorMessage } from "@assets/utilities/translateUtilities";
import { WithFormController } from "@components";
import UserService from "@services/UserService";

interface BookingPartySectionProps {
  control: Control<DomesticFormTypes.CreateForm>;
  departments: DomesticTypes.DomesticDepartment[];
  errors: FieldErrorsImpl<DeepRequired<DomesticFormTypes.CreateForm>>;
  /** Whether or not this BookingSection is the user's section. */
  isUserParty: boolean;
  onBookingPartyTypeChange: CommonTypes.CallbackFunction;
  onCustomerChange: (newCustomer: DomesticTypes.DomesticCustomer) => void;
  onDepartmentChange: (newDepartment: DomesticTypes.DomesticDepartment) => void;
  onNewAddressToggle: (isChecked: boolean) => void;
  onPersonSSNChange: (personSSN: string) => void;
  postalCodes: DomesticTypes.DomesticPostCode[];
  register: UseFormRegister<DomesticFormTypes.CreateForm>;
  /** The registered field names in the react-hook-form. */
  registeredFields: DomesticFormTypes.BookingPartySectionFields;
  searchCustomers: (
    customerId: string
  ) => Promise<DomesticTypes.DomesticCustomer[]>;
  showDepartments: boolean;
  title?: string;
  userProfile?: UserProfileViewModel;
}

const BookingPartySection = ({
  control,
  departments,
  errors,
  isUserParty,
  onBookingPartyTypeChange,
  onCustomerChange,
  onDepartmentChange,
  onNewAddressToggle,
  onPersonSSNChange,
  postalCodes,
  register,
  registeredFields,
  searchCustomers,
  showDepartments,
  title,
}: BookingPartySectionProps) => {
  const { t } = useTranslation();

  const bookingPartyType: DomesticConstants.BOOKING_PARTY_TYPE = useWatch({
    control,
    name: registeredFields.BookingPartyType,
  });

  const selectedCustomer: DomesticTypes.DomesticCustomer | null = useWatch({
    control,
    name: registeredFields.Customer,
  });

  const isNewAddress: boolean = useWatch({
    control,
    name: registeredFields.IsNewAddress,
  });

  const isUserEmployeeOrAgent =
    UserService.isUserEmployee() || UserService.isUserAgent();

  const isBookingPartyCompany =
    bookingPartyType === DomesticConstants.BOOKING_PARTY_TYPE.COMPANY;
  const isBookingPartyOfCustomerUser =
    UserService.isUserCustomer() && isUserParty;

  const isCustomerSelectable = isUserEmployeeOrAgent || isBookingPartyCompany;

  const isAddressReadOnly = !isNewAddress && isCustomerSelectable;

  const isContactReadOnly =
    isCustomerSelectable &&
    !isNewAddress &&
    (!selectedCustomer ||
      (selectedCustomer &&
        selectedCustomer.Source === DomesticConstants.CUSTOMER_SOURCE.SINNA));

  const getPersonNameFields = () => (
    <>
      <Input
        errorMessage={getErrorMessage(registeredFields.Name, errors)}
        fullWidth
        hasError={errors[registeredFields.Name] != null}
        id={registeredFields.Name}
        label={t("LABEL_NAME")}
        maxLength={DomesticConstants.INPUT_CONSTRAINTS.MAX_NAME_LENGTH}
        placeholder={t("PLACEHOLDER_NAME")}
        required
        {...register(registeredFields.Name)}
      />
      <WithFormController control={control} name={registeredFields.SSN}>
        <Input
          errorMessage={getErrorMessage(registeredFields.SSN, errors)}
          fullWidth
          hasError={errors[registeredFields.SSN] != null}
          id={registeredFields.SSN}
          label={t("LABEL_SSN")}
          maxLength={DomesticConstants.INPUT_CONSTRAINTS.SSN_LENGTH}
          onChange={(e) => onPersonSSNChange(e.target.value)}
          onlyDigits
          placeholder={t("PLACEHOLDER_SSN")}
          required
        />
      </WithFormController>
    </>
  );

  const getCustomerDropdown = () => (
    <WithFormController
      control={control}
      key={bookingPartyType}
      name={registeredFields.Customer}
    >
      <AsyncSelect
        defaultOptionsMessage={t(
          LabelConstants.SELECT_LABELS.DEFAULT_OPTIONS_MESSAGE
        )}
        errorMessage={getErrorMessage(registeredFields.Customer, errors)}
        fullWidth
        getOptionLabel={customerCompanyLabelMapper}
        getOptionValue={customerCompanyValueMapper}
        hasError={errors[registeredFields.Customer] != null}
        id={registeredFields.Customer}
        isClearable={false}
        label={isBookingPartyCompany ? t("LABEL_COMPANY") : t("LABEL_PERSON")}
        loadOptions={searchCustomers}
        noOptionsMessage={t(LabelConstants.SELECT_LABELS.NO_OPTIONS)}
        onChange={onCustomerChange}
        placeholder={
          isBookingPartyCompany
            ? t("PLACEHOLDER_SEARCH_COMPANY")
            : t("PLACEHOLDER_SEARCH_PERSON")
        }
        readOnly={isBookingPartyOfCustomerUser}
        required
      />
    </WithFormController>
  );

  /** Returns either the customer dropdown, and/or the SSN related fields. */
  const getCustomerRelatedSection = () => {
    if (isBookingPartyCompany || isUserEmployeeOrAgent) {
      return getCustomerDropdown();
    }
    return getPersonNameFields();
  };

  return (
    <Section title={title}>
      {!isBookingPartyOfCustomerUser && (
        <Group onChange={onBookingPartyTypeChange}>
          <CheckForm
            bordered
            id={`${registeredFields.BookingPartyType}-COMPANY`}
            label={t("LABEL_COMPANY")}
            type="radio"
            value={DomesticConstants.BOOKING_PARTY_TYPE.COMPANY}
            {...register(registeredFields.BookingPartyType)}
          />
          <CheckForm
            bordered
            id={`${registeredFields.BookingPartyType}-PERSON`}
            label={t("LABEL_PERSON")}
            type="radio"
            value={DomesticConstants.BOOKING_PARTY_TYPE.PERSON}
            {...register(registeredFields.BookingPartyType)}
          />
        </Group>
      )}

      <Typography>
        {t(
          isBookingPartyOfCustomerUser && selectedCustomer
            ? DomesticConstants.TRANSLATION_LABELS.CUSTOMER_AUTOCOMPLETE
            : isBookingPartyCompany
            ? DomesticConstants.TRANSLATION_LABELS.COMPANY_AUTOCOMPLETE
            : DomesticConstants.TRANSLATION_LABELS.PERSON_DETAILS
        )}
      </Typography>

      {getCustomerRelatedSection()}

      {isCustomerSelectable && (
        <Group vertical>
          <Typography variant="info">
            {t(
              showDepartments
                ? DomesticConstants.TRANSLATION_LABELS.DEPARTMENT_PREFILL
                : DomesticConstants.TRANSLATION_LABELS.ADDRESS_PREFILL
            )}
          </Typography>
          <CheckForm
            id={`${registeredFields.BookingPartyType}-new-address`}
            label={t("LABEL_USE_DIFFERENT_ADDRESS")}
            {...register(registeredFields.IsNewAddress, {
              onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                onNewAddressToggle(e.target.checked);
              },
            })}
          />
        </Group>
      )}

      {isBookingPartyCompany && showDepartments && !isNewAddress && (
        <WithFormController
          control={control}
          name={registeredFields.Department}
        >
          <Select
            errorMessage={getErrorMessage(registeredFields.Department, errors)}
            fullWidth
            getOptionLabel={departmentLabelMapper}
            getOptionValue={departmentValueMapper}
            hasError={errors[registeredFields.Department] != null}
            id={registeredFields.Department}
            isClearable
            label={t("LABEL_DEPARTMENT")}
            onChange={(department: DomesticTypes.DomesticDepartment) => {
              onDepartmentChange(department);
            }}
            options={departments}
            placeholder={t("PLACEHOLDER_SELECT_DEPARTMENT")}
            required
          />
        </WithFormController>
      )}

      <Grid mdCol={3}>
        {/* ---- Address ---- */}
        <Input
          className="col-span-2"
          errorMessage={getErrorMessage(registeredFields.Address, errors)}
          fullWidth
          hasError={errors[registeredFields.Address] != null}
          id={registeredFields.Address}
          label={t("LABEL_ADDRESS")}
          maxLength={DomesticConstants.INPUT_CONSTRAINTS.MAX_ADDRESS_LENGTH}
          placeholder={t("PLACEHOLDER_ADDRESS")}
          readOnly={isAddressReadOnly}
          required
          {...register(registeredFields.Address)}
        />
        <WithFormController
          control={control}
          name={registeredFields.PostalCode}
        >
          <Select
            errorMessage={getErrorMessage(registeredFields.PostalCode, errors)}
            fullWidth
            getOptionLabel={postCodeLabelMapper}
            getOptionValue={postCodeValueMapper}
            hasError={errors[registeredFields.PostalCode] != null}
            id={registeredFields.PostalCode}
            label={t("LABEL_POSTALCODE")}
            menuFitLongestOption
            options={postalCodes}
            placeholder={t("PLACEHOLDER_SELECT")}
            portaling
            readOnly={isAddressReadOnly}
            required
          />
        </WithFormController>

        {/* ---- Contact ---- */}
        <Input
          className="col-span-2"
          errorMessage={getErrorMessage(registeredFields.Email, errors)}
          fullWidth
          hasError={errors[registeredFields.Email] != null}
          id={registeredFields.Email}
          label={t("LABEL_EMAIL")}
          maxLength={DomesticConstants.INPUT_CONSTRAINTS.MAX_EMAIL_LENGTH}
          placeholder={t("PLACEHOLDER_EMAIL")}
          readOnly={isContactReadOnly}
          {...register(registeredFields.Email)}
        />

        <Input
          errorMessage={getErrorMessage(registeredFields.Phone, errors)}
          fullWidth
          hasError={errors[registeredFields.Phone] != null}
          id={registeredFields.Phone}
          label={t("LABEL_PHONE")}
          maxLength={
            DomesticConstants.INPUT_CONSTRAINTS.MAX_PHONE_NUMBER_LENGTH
          }
          placeholder={t("PLACEHOLDER_PHONE")}
          readOnly={isContactReadOnly}
          required
          {...register(registeredFields.Phone)}
        />
      </Grid>
    </Section>
  );
};

export default BookingPartySection;
